import { Minus, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import type { TableZoom } from '@/lib/hooks/useTableZoom';

interface TableZoomControlsProps {
  zoom: TableZoom;
}

/**
 * Compact segmented − / % / + control that scales the tables (browser Ctrl +/-
 * style). Styled to match the outline `Button size="sm"` (Columns / Filter) that
 * sits beside it — same height, border, radius and hover tokens. Clicking the
 * percentage resets to 100%.
 */
export function TableZoomControls({ zoom }: TableZoomControlsProps) {
  const { t } = useTranslation();
  const { zoom: level, zoomIn, zoomOut, reset, canZoomIn, canZoomOut } = zoom;

  const stepClass =
    'flex h-full w-8 items-center justify-center text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-40';

  return (
    <div
      role="group"
      aria-label={t('tableZoom.label')}
      className="inline-flex h-8 shrink-0 items-center overflow-hidden rounded-md border border-input bg-background"
    >
      <button
        type="button"
        onClick={zoomOut}
        disabled={!canZoomOut}
        title={t('tableZoom.zoomOut')}
        aria-label={t('tableZoom.zoomOut')}
        className={cn(stepClass, 'rounded-l-md')}
      >
        <Minus className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={reset}
        title={t('tableZoom.reset')}
        aria-label={t('tableZoom.reset')}
        className="h-full min-w-[2.75rem] border-x border-input px-1 text-center text-xs font-medium tabular-nums text-foreground transition-colors hover:bg-accent"
      >
        {Math.round(level * 100)}%
      </button>
      <button
        type="button"
        onClick={zoomIn}
        disabled={!canZoomIn}
        title={t('tableZoom.zoomIn')}
        aria-label={t('tableZoom.zoomIn')}
        className={cn(stepClass, 'rounded-r-md')}
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
